import { Product } from '@/services/api/contracts/catalog/types';
import { useCategories } from '@/services/api/hooks/catalog';
import {
  groupProductsByCategories,
  useProductsByCategory,
  useProductsGroupedUpByCategory
} from '@/services/api/hooks/product';
import { BasicBetterBest } from '@/services/catalogue/useBasicBetterBest';
import { useBbbMode, useRegionFilter } from '@/services/preferences/useLocalPreferences';
import { useSearch } from '@/utils/search';

export interface SpecFilterGroup {
  queries: string[];
}

export interface ProductsOptions {
  searchQuery?: string;
  specFilter?: SpecFilterGroup[];
  filterProductIds?: string[];
  manufacturerFilter?: string[];
  applyRegionFilter?: boolean;
  excludeBBBFilter?: boolean;
  effectiveDate?: Date;
}

const useFilteredProducts = (products: Product[], opts?: ProductsOptions) => {
  const { bbbMode } = useBbbMode();
  const { regionId } = useRegionFilter();
  const { searchQuery, specFilter, filterProductIds, manufacturerFilter } = opts || {};

  let filteredProducts = [...products];

  if (filterProductIds && filterProductIds.length > 0) {
    filteredProducts = filteredProducts.filter(p => filterProductIds.includes(p.id));
  }

  if (opts?.applyRegionFilter && regionId) {
    filteredProducts = filteredProducts.filter(p => p.region.id === regionId);
  }

  if (bbbMode && !opts?.excludeBBBFilter) {
    const bbbFilteredProducts = filteredProducts.filter(p => {
      return p.propertyBag.basicBetterBest && p.propertyBag.basicBetterBest !== BasicBetterBest.UNSET;
    });

    filteredProducts = bbbFilteredProducts;
  }

  filteredProducts = useSearch(filteredProducts, ['name', 'manufacturer.name'], searchQuery);

  if (specFilter) {
    const specFilteredProducts: Product[] = [];
    filteredProducts.forEach(product => {
      let matchesAllFilters = true;
      specFilter.forEach(group => {
        const matchesCurrentFilter = !!group.queries.find(query => {
          return !!product.propertyBag.properties.find(property => property.text === query);
        });

        if (!matchesCurrentFilter) {
          matchesAllFilters = false;
        }
      });

      if (matchesAllFilters) {
        specFilteredProducts.push(product);
      }
    });

    filteredProducts = specFilteredProducts;
  }

  if (manufacturerFilter && manufacturerFilter.length > 0) {
    filteredProducts = filteredProducts.filter(p => manufacturerFilter.includes(p.manufacturer.id));
  }

  return filteredProducts;
};

export const useCategoryGroupedProducts = (opts?: ProductsOptions) => {
  const [categories] = useCategories();
  const groupedUpProducts = useProductsGroupedUpByCategory(opts?.effectiveDate);

  const flatProducts = Object.values(groupedUpProducts).flatMap(items => items) as Product[];

  const filteredProducts = useFilteredProducts(flatProducts, opts);

  const regroupedProducts = groupProductsByCategories(filteredProducts, categories);

  if (opts?.filterProductIds && opts.filterProductIds.length === 0) return {};

  return regroupedProducts;
};

export const useCategoryProducts = (categoryId: string, opts?: ProductsOptions) => {
  const [products] = useProductsByCategory(categoryId);

  const filteredProducts = useFilteredProducts(products, opts);

  return filteredProducts;
};
